<script setup>
import { ref, computed, watch } from "vue";
import MIcon from "@/components/m/MIcon.vue";

/* Pull-to-refresh scroll container. Emits "refresh" when the user pulls past
   the threshold. If the parent passes :busy (its loading ref), the spinner
   stays until the real fetch finishes; otherwise it falls back to a short
   fixed spin. */
const props = defineProps({
  busy: { type: Boolean, default: null },
});
const emit = defineEmits(["refresh", "load-more"]);

const MIN_SPIN = 500; // avoid a jarring flash when a fetch resolves instantly
const THRESH = 64;
const scrollEl = ref(null);
let spinStart = 0;

function finishRefresh() {
  refreshing.value = false;
  pull.value = 0;
}

// When the parent reports its fetch is done (busy true→false), stop the spinner
// once a minimum display time has passed.
watch(
  () => props.busy,
  (b, prev) => {
    if (!refreshing.value || b || !prev) return;
    const remaining = MIN_SPIN - (Date.now() - spinStart);
    if (remaining > 0) setTimeout(finishRefresh, remaining);
    else finishRefresh();
  },
);

// Emit "load-more" as the user nears the bottom; the parent guards it with its
// own hasMore/loadingMore state, so spurious emits are cheap no-ops.
const onScroll = () => {
  const el = scrollEl.value;
  if (!el) return;
  if (el.scrollTop + el.clientHeight >= el.scrollHeight - 280) {
    emit("load-more");
  }
};
const pull = ref(0);
const refreshing = ref(false);
const start = ref(null);

const onStart = (e) => {
  if (refreshing.value) return;
  const el = scrollEl.value;
  if (el && el.scrollTop <= 0) {
    start.value = e.touches ? e.touches[0].clientY : e.clientY;
  } else {
    start.value = null;
  }
};
const onMove = (e) => {
  if (start.value == null || refreshing.value) return;
  const y = e.touches ? e.touches[0].clientY : e.clientY;
  const d = y - start.value;
  if (d > 0) pull.value = Math.min(THRESH * 1.6, d * 0.5);
};
const onEnd = async () => {
  if (start.value == null) return;
  start.value = null;
  if (pull.value >= THRESH && !refreshing.value) {
    refreshing.value = true;
    pull.value = THRESH * 0.7;
    spinStart = Date.now();
    emit("refresh");
    if (props.busy === null) {
      // No busy signal from the parent — fall back to a short fixed spin.
      await new Promise((r) => setTimeout(r, 900));
      finishRefresh();
    } else {
      // The busy watcher clears the spinner when the fetch resolves; this is a
      // backstop in case the parent never toggles busy (e.g. an early return).
      setTimeout(() => refreshing.value && finishRefresh(), 8000);
    }
  } else {
    pull.value = 0;
  }
};

const spinnerRotate = computed(
  () => `rotate(${Math.min(180, (pull.value / THRESH) * 180)}deg)`,
);
const spinnerColor = computed(() =>
  pull.value >= THRESH ? "var(--brand-strong)" : "var(--text-3)",
);
const contentStyle = computed(() => ({
  transform: pull.value ? `translateY(${pull.value}px)` : "none",
  transition: start.value == null ? "transform .25s" : "none",
}));
</script>

<template>
  <div
    ref="scrollEl"
    class="btm-scroll"
    @scroll.passive="onScroll"
    @touchstart="onStart"
    @touchmove="onMove"
    @touchend="onEnd"
    @pointerdown="onStart"
    @pointermove="(e) => (e.pressure > 0 || e.buttons) && onMove(e)"
    @pointerup="onEnd"
  >
    <div
      class="btm-ptr"
      :class="{ spin: refreshing }"
      :style="{ height: pull + 'px', opacity: pull > 6 ? 1 : 0 }"
    >
      <div style="position: absolute; bottom: 8px">
        <MIcon v-if="refreshing" name="settings" :size="20" />
        <svg
          v-else
          width="20"
          height="20"
          viewBox="0 0 24 24"
          :style="{ transform: spinnerRotate, color: spinnerColor }"
          fill="none"
          stroke="currentColor"
          stroke-width="2.2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M12 5v14M5 12l7 7 7-7" />
        </svg>
      </div>
    </div>
    <div :style="contentStyle"><slot /></div>
  </div>
</template>
